import { FloraIntegrationSource } from '../../api/agents-client/agent-client.type';
import type { ApprovalMode, FloraModelOption } from '../../api/agents-client/agent-client.type';
import type { OrganizationResponse } from '../../api/organization-client/organization-client.type';
import type { Portfolio } from '../../api/portfolio-client/portfolio-client.type';
import type { ProjectsResponse } from '../../api/projects-client/projects-client.type';
import { FloraVersion } from '../../containers/flora/flora.type';
import { updateI18nResources } from '../../helpers/i18n-helpers/i18n.helpers';
import { useGlobalStore } from './global-store';
import type { State, SuperuserMode } from './global-store.type';

/**
 * Sets the organization in the global store.
 *
 * @param {OrganizationResponse | null} organization - The organization to set. Can be null.
 * @return {void}
 */
const setOrganization = (organization: OrganizationResponse | null): void => {
  updateI18nResources(organization);
  useGlobalStore.setState({ organization }, false, '[Global Store] setOrganization');
};

/**
 * Sets the organizations in the global store.
 *
 * @param {OrganizationResponse[]} organizations - An array of OrganizationResponse objects representing the organizations to be set.
 * @return {void}
 */
const setOrganizations = (organizations: OrganizationResponse[]): void => {
  useGlobalStore.setState({ organizations }, false, '[Global Store] setOrganizations');
};

/**
 * Sets the portfolio in the global store.
 *
 * @param {Portfolio | null} portfolio - The portfolio to set. Can be null.
 * @return {void}
 */
const setPortfolio = (portfolio: Portfolio | null): void => {
  useGlobalStore.setState({ portfolio }, false, '[Global Store] setPortfolio');
};

/**
 * Sets the portfolios in the global store.
 *
 * @param {Portfolio[]} portfolios - An array of Portfolio objects representing the portfolios to be set.
 * @return {void}
 */
const setPortfolios = (portfolios: Portfolio[]): void => {
  useGlobalStore.setState({ portfolios }, false, '[Global Store] setPortfolios');
};

/**
 * Sets the teams in the global store.
 *
 * @param {ProjectsResponse[]} teams - An array of ProjectsResponse objects representing the teams to be set.
 * @return {void}
 */
const setTeams = (teams: ProjectsResponse[]): void => {
  useGlobalStore.setState({ teams }, false, '[Global Store] setTeams');
};

/**
 * Sets the useAI in the global store.
 *
 * @param {boolean} useAI - The boolean value to set.
 * @return {void}
 */
const setUseAI = (useAI: boolean): void => {
  useGlobalStore.setState({ useAI }, false, '[Global Store] setUseAI');
};

/**
 * Marks the canUseAI check as settled for this session. Until this flips true,
 * Flora surfaces must render a loading state rather than an access-denied
 * state, since `useAI` still holds its pre-check default.
 *
 * @param {boolean} useAIResolved - True once the canUseAI query has succeeded or failed
 * @param {string | null} organizationId - Organization covered by the settled check
 * @return {void}
 */
const setUseAIResolved = (useAIResolved: boolean, organizationId: string | null = null): void => {
  useGlobalStore.setState(
    {
      useAIResolved,
      useAIResolvedOrganizationId: useAIResolved ? organizationId : null,
    },
    false,
    '[Global Store] setUseAIResolved',
  );
};

/**
 * Sets the flora integration statuses in the global store.
 *
 * @param {Record<FloraIntegrationSource, boolean>} floraIntegrationStatuses - The flora integration statuses to set.
 * @return {void}
 */
const setFloraIntegrationStatuses = (floraIntegrationStatuses: Record<FloraIntegrationSource, boolean>): void => {
  useGlobalStore.setState({ floraIntegrationStatuses }, false, '[Global Store] setFloraIntegrationStatuses');
};

const setFloraVersionsAvailable = (floraVersionsAvailable: FloraVersion[]): void => {
  useGlobalStore.setState({ floraVersionsAvailable }, false, '[Global Store] setFloraVersionsAvailable');
};

/**
 * Sets the current flora version in the global store.
 *
 * @param {FloraVersion} floraVersion - Flora version identifier
 * @return {void}
 */
const setFloraVersion = (floraVersion: FloraVersion): void => {
  useGlobalStore.setState({ floraVersion }, false, '[Global Store] setFloraVersion');
};

/**
 * Sets the selected Flora model identifier in the global store.
 *
 * @param {string} floraModel - The model id to use for Flora requests. Empty string means use the backend default.
 * @return {void}
 */
const setFloraModel = (floraModel: string): void => {
  useGlobalStore.setState({ floraModel }, false, '[Global Store] setFloraModel');
};

/**
 * Sets the selected Flora reasoning effort in the global store.
 *
 * @param {string | null} floraEffort - The effort level to use for Flora requests. Null means use the model default.
 * @return {void}
 */
const setFloraEffort = (floraEffort: string | null): void => {
  useGlobalStore.setState({ floraEffort }, false, '[Global Store] setFloraEffort');
};

/**
 * Records the Ask/Auto approval mode chosen from the Flora composer for one chat.
 *
 * An existing chat (with a thread id) stores the choice under that thread, so
 * every Flora surface showing the chat agrees on it; a brand-new chat stores it
 * as the draft until the first message creates the thread.
 *
 * @param {string | null | undefined} threadId - The chat's thread id; empty for a new chat
 * @param {ApprovalMode} approvalMode - The mode the user picked
 * @return {void}
 */
const setFloraChatApprovalMode = (threadId: string | null | undefined, approvalMode: ApprovalMode): void => {
  if (!threadId) {
    useGlobalStore.setState(
      { floraDraftApprovalMode: approvalMode },
      false,
      '[Global Store] setFloraChatApprovalMode (draft)',
    );
    return;
  }
  useGlobalStore.setState(
    (state) => ({ floraChatApprovalModes: { ...state.floraChatApprovalModes, [threadId]: approvalMode } }),
    false,
    '[Global Store] setFloraChatApprovalMode',
  );
};

/**
 * Moves a new chat's draft Ask/Auto choice onto the thread its first message created.
 * No-op when there is no draft, so it is safe to call on every new thread id.
 *
 * @param {string} threadId - The newly created chat's thread id
 * @return {void}
 */
const adoptFloraDraftApprovalMode = (threadId: string): void => {
  useGlobalStore.setState(
    (state) =>
      state.floraDraftApprovalMode
        ? {
            floraChatApprovalModes: { ...state.floraChatApprovalModes, [threadId]: state.floraDraftApprovalMode },
            floraDraftApprovalMode: null,
          }
        : {},
    false,
    '[Global Store] adoptFloraDraftApprovalMode',
  );
};

/**
 * Clears the draft Ask/Auto choice so the next new chat starts on the saved default.
 *
 * @return {void}
 */
const clearFloraDraftApprovalMode = (): void => {
  useGlobalStore.setState({ floraDraftApprovalMode: null }, false, '[Global Store] clearFloraDraftApprovalMode');
};

/**
 * Seeds per-chat Ask/Auto choices from the chat histories the backend returned.
 * A choice already made in this tab wins, since it may not have been sent yet
 * (the backend only saves a switch with the chat's next message).
 *
 * @param {Array<{ id: string; approval_mode?: ApprovalMode | null }>} histories - Chat histories from the backend
 * @return {void}
 */
const seedFloraChatApprovalModes = (histories: { id: string; approval_mode?: ApprovalMode | null }[]): void => {
  const saved: Record<string, ApprovalMode> = {};
  for (const history of histories) {
    if (history.approval_mode) {
      saved[history.id] = history.approval_mode;
    }
  }
  if (Object.keys(saved).length === 0) {
    return;
  }
  useGlobalStore.setState(
    (state) => ({ floraChatApprovalModes: { ...saved, ...state.floraChatApprovalModes } }),
    false,
    '[Global Store] seedFloraChatApprovalModes',
  );
};

/**
 * Sets the list of Flora model options available for selection in the global store.
 *
 * @param {FloraModelOption[]} floraModelsAvailable - The available Flora model options returned by the backend.
 * @return {void}
 */
const setFloraModelsAvailable = (floraModelsAvailable: FloraModelOption[]): void => {
  useGlobalStore.setState({ floraModelsAvailable }, false, '[Global Store] setFloraModelsAvailable');
};

/**
 * Sets whether the sidebar is open.
 *
 * @param {boolean} isOpen - Whether the sidebar should be open
 * @return {void}
 */
const setSidebarOpen = (isOpen: boolean): void => {
  useGlobalStore.setState({ isSidebarOpen: isOpen }, false, '[Global Store] setSidebarOpen');
};

/**
 * Sets the superuser mode in the global store.
 *
 * Responsibilities:
 * 1. Merge the partial update into the current superuser mode flags
 * 2. Detect superuser mode (or the model selection switch) turning off
 * 3. On turn-off, reset the Flora model/effort selection so a custom model
 *    cannot stay on the wire after the picker is hidden — back to the
 *    catalog default when one is available, otherwise cleared entirely
 *
 * @param {Partial<SuperuserMode>} superuserMode - The superuser mode flags to merge.
 * @return {void}
 */
const setSuperuserMode = (superuserMode: Partial<SuperuserMode>): void => {
  const currentState = useGlobalStore.getState();
  const previousMode = currentState.superuserMode;
  const nextMode = { ...previousMode, ...superuserMode };

  const modeTurnedOff = previousMode.enabled && !nextMode.enabled;
  const selectionTurnedOff = previousMode.showModelSelection && !nextMode.showModelSelection;

  const updates: Partial<State> = { superuserMode: nextMode };
  if (modeTurnedOff || selectionTurnedOff) {
    const defaultOption =
      currentState.floraModelsAvailable.find((option) => option.is_default) ?? currentState.floraModelsAvailable[0];
    updates.floraModel = defaultOption?.model_id ?? '';
    updates.floraEffort = defaultOption?.default_effort ?? null;
  }

  useGlobalStore.setState(updates, false, '[Global Store] setSuperuserMode');
};

/**
 * Initializes the global state with the given organizations and portfolios on application load.
 *
 * @param {OrganizationResponse[]} organizations - An array of OrganizationResponse objects representing the organizations.
 * @param {Portfolio[]} portfolios - An array of Portfolio objects representing the portfolios.
 * @return {void}
 */
const initGlobalState = (organizationsData: OrganizationResponse[], portfolios: Portfolio[]): void => {
  const organizations = organizationsData.filter((organization) =>
    portfolios.some((portfolio) => portfolio.organization.id === organization.id),
  );

  const currentState = useGlobalStore.getState();
  const currentOrganization = currentState.organization;
  const currentPortfolio = currentState.portfolio;

  // Revalidate the persisted singular scope against the fresh payload: an admin
  // may have deleted the portfolio, moved it to a different org, or revoked the
  // user's access since the last session. Looking up by id in the fresh arrays
  // both refreshes nested data (feature_flags, portfolio.projects) and catches
  // stale references.
  const refreshedOrganization = currentOrganization
    ? (organizations.find(({ id }) => id === currentOrganization.id) ?? null)
    : null;
  const refreshedPortfolio = currentPortfolio
    ? (portfolios.find(({ id }) => id === currentPortfolio.id) ?? null)
    : null;

  const isValidScope =
    !!refreshedOrganization && !!refreshedPortfolio && refreshedPortfolio.organization.id === refreshedOrganization.id;

  let nextOrganization: OrganizationResponse | null;
  let nextPortfolio: Portfolio | null;

  if (isValidScope) {
    nextOrganization = refreshedOrganization;
    nextPortfolio = refreshedPortfolio;
  } else if (refreshedOrganization) {
    // Org survived but the persisted portfolio did not (deleted, reassigned, or
    // access revoked). Keep the org and pick a default portfolio for it.
    const organizationPortfolios = portfolios.filter(
      (portfolio) => portfolio.organization.id === refreshedOrganization.id,
    );
    nextOrganization = refreshedOrganization;
    nextPortfolio =
      organizationPortfolios.find((portfolio) => portfolio.is_default) ?? organizationPortfolios[0] ?? null;
  } else {
    const defaultOrganization = organizations[0] ?? null;
    if (defaultOrganization) {
      const organizationPortfolios = portfolios.filter(
        (portfolio) => portfolio.organization.id === defaultOrganization.id,
      );
      nextOrganization = defaultOrganization;
      nextPortfolio =
        organizationPortfolios.find((portfolio) => portfolio.is_default) ?? organizationPortfolios[0] ?? null;
    } else {
      nextOrganization = null;
      nextPortfolio = null;
    }
  }

  // Single atomic setState: `initialized: true` flips in the same update that writes fresh
  // singular `organization`/`portfolio`, so subscribers (e.g. FloraProvider) that gate on
  // `initialized` never observe a stale-scope / initialized-true intermediate snapshot.
  useGlobalStore.setState(
    {
      organizations,
      portfolios,
      organization: nextOrganization,
      portfolio: nextPortfolio,
      initialized: true,
    },
    false,
    '[Global Store] initGlobalState',
  );

  updateI18nResources(nextOrganization);
};

export {
  adoptFloraDraftApprovalMode,
  clearFloraDraftApprovalMode,
  initGlobalState,
  seedFloraChatApprovalModes,
  setFloraChatApprovalMode,
  setFloraEffort,
  setFloraIntegrationStatuses,
  setFloraModel,
  setFloraModelsAvailable,
  setFloraVersion,
  setFloraVersionsAvailable,
  setOrganization,
  setOrganizations,
  setPortfolio,
  setPortfolios,
  setSidebarOpen,
  setSuperuserMode,
  setTeams,
  setUseAI,
  setUseAIResolved,
};
